import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { WordNode } from "./WordNode";

export type TranscriptPlaybackEditMode = "delete" | "strikethrough" | "retake-strikethrough";

export type SelectionCutRun = {
    startSec: number;
    endSec: number;
    sourceIndex: number | null;
    prevWordEndSec: number | null;
    nextWordStartSec: number | null;
    nodeKeys: string[];
};

export type SelectionCutPlan = {
    startSec: number;
    endSec: number;
    nodeKeys: string[];
    runs: SelectionCutRun[];
};

type WordEntry = {
    key: string;
    startSec: number;
    endSec: number;
    sourceIndex: number | null;
    orderIndex: number;
};

const toWordEntry = (node: WordNode, orderIndex: number): WordEntry | null => {
    const startSec = Number(node.__payload.startSec);
    if (!Number.isFinite(startSec)) return null;

    const endRaw = Number(node.__payload.endSec);
    const endSec = Number.isFinite(endRaw) ? Math.max(startSec, endRaw) : startSec;
    const sourceRaw = Number(node.__payload.sourceIndex);
    const sourceIndex = Number.isFinite(sourceRaw) ? Math.floor(sourceRaw) : null;

    return {
        key: node.getKey(),
        startSec,
        endSec,
        sourceIndex,
        orderIndex,
    };
};

const compareEntries = (a: WordEntry, b: WordEntry): number => {
    if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex;
    if (a.startSec !== b.startSec) return a.startSec - b.startSec;
    if (a.endSec !== b.endSec) return a.endSec - b.endSec;
    return a.key.localeCompare(b.key);
};

export const buildSelectionCutPlan = (
    selectedNodes: WordNode[],
    allWordNodes?: WordNode[]
): SelectionCutPlan | null => {
    if (!Array.isArray(selectedNodes) || selectedNodes.length === 0) return null;

    const orderedAllEntries: WordEntry[] = [];
    const sourceEntriesByKey = new Map<string, WordEntry>();
    const sourceNodes = Array.isArray(allWordNodes) && allWordNodes.length > 0 ? allWordNodes : selectedNodes;

    for (let i = 0; i < sourceNodes.length; i++) {
        const entry = toWordEntry(sourceNodes[i], i);
        if (!entry || sourceEntriesByKey.has(entry.key)) continue;
        sourceEntriesByKey.set(entry.key, entry);
        orderedAllEntries.push({ ...entry, orderIndex: orderedAllEntries.length });
    }

    const selectedEntries: WordEntry[] = [];
    const seenSelected = new Set<string>();
    for (let i = 0; i < selectedNodes.length; i++) {
        const fallback = toWordEntry(selectedNodes[i], Number.MAX_SAFE_INTEGER - selectedNodes.length + i);
        if (!fallback || seenSelected.has(fallback.key)) continue;
        seenSelected.add(fallback.key);
        const existing = sourceEntriesByKey.get(fallback.key);
        selectedEntries.push(existing ?? fallback);
    }

    if (selectedEntries.length === 0) return null;
    selectedEntries.sort(compareEntries);

    const runs: Array<SelectionCutRun & { firstOrderIndex: number; lastOrderIndex: number }> = [];
    for (const entry of selectedEntries) {
        const prev = runs[runs.length - 1];
        const isContiguousWithPrev =
            Boolean(prev) &&
            prev.sourceIndex === entry.sourceIndex &&
            entry.orderIndex === prev.lastOrderIndex + 1;

        if (!isContiguousWithPrev) {
            runs.push({
                startSec: entry.startSec,
                endSec: entry.endSec,
                sourceIndex: entry.sourceIndex,
                prevWordEndSec: null,
                nextWordStartSec: null,
                nodeKeys: [entry.key],
                firstOrderIndex: entry.orderIndex,
                lastOrderIndex: entry.orderIndex,
            });
            continue;
        }

        prev.endSec = Math.max(prev.endSec, entry.endSec);
        prev.nodeKeys.push(entry.key);
        prev.lastOrderIndex = entry.orderIndex;
    }

    for (const run of runs) {
        if (run.sourceIndex == null) continue;

        for (let i = run.firstOrderIndex - 1; i >= 0; i--) {
            const candidate = orderedAllEntries[i];
            if (candidate?.sourceIndex === run.sourceIndex) {
                run.prevWordEndSec = candidate.endSec;
                break;
            }
        }

        for (let i = run.lastOrderIndex + 1; i < orderedAllEntries.length; i++) {
            const candidate = orderedAllEntries[i];
            if (candidate?.sourceIndex === run.sourceIndex) {
                run.nextWordStartSec = candidate.startSec;
                break;
            }
        }
    }

    return {
        startSec: Math.min(...selectedEntries.map((entry) => entry.startSec)),
        endSec: Math.max(...selectedEntries.map((entry) => entry.endSec)),
        nodeKeys: selectedEntries.map((entry) => entry.key),
        runs: runs.map(({ firstOrderIndex: _firstOrderIndex, lastOrderIndex: _lastOrderIndex, ...run }) => run),
    };
};

export const applySelectionCutPlan = async (
    plan: SelectionCutPlan,
    transcriptMode: TranscriptPlaybackEditMode
): Promise<void> => {
    if (!plan || !Array.isArray(plan.runs) || plan.runs.length === 0) return;

    const orderedRuns = [...plan.runs].sort((a, b) => {
        if (a.startSec !== b.startSec) return b.startSec - a.startSec;
        return b.endSec - a.endSec;
    });

    CompositionService.beginScriptLayerBatch();
    try {
        for (const run of orderedRuns) {
            if (!(Number.isFinite(run.startSec) && Number.isFinite(run.endSec) && run.endSec > run.startSec)) continue;

            const sourceIndex = run.sourceIndex;
            if (sourceIndex != null) {
                try {
                    await CompositionService.cutScriptLayerRangeWithAnalysis(
                        run.startSec,
                        run.endSec,
                        sourceIndex,
                        {
                            transcriptMode,
                            prevWordEndSec: run.prevWordEndSec,
                            nextWordStartSec: run.nextWordStartSec,
                        }
                    );
                    continue;
                } catch (err) {
                    console.warn("[selectionCutPlan] Analyzed cut failed; falling back to plain cut:", err);
                }
            }

            CompositionService.cutScriptLayerRange(run.startSec, run.endSec, { transcriptMode });
        }
    } finally {
        await CompositionService.endScriptLayerBatch();
    }
};
